import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, Percent, TrendingUp, Calculator, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { formatCurrency, formatPercentage } from './financingCalculations';

const cards = (monthlyPayment, interestRate, totalInterest, totalCost) => [
  {
    label: 'Monthly Payment',
    value: formatCurrency(monthlyPayment),
    icon: DollarSign,
    iconColor: 'text-succession-success',
    helperText: 'Principal + Interest',
  },
  {
    label: 'Interest Rate',
    value: formatPercentage(interestRate),
    icon: Percent,
    iconColor: 'text-succession-primary',
    helperText: 'Annual percentage rate',
    tooltip: 'Based on loan amount band',
  },
  {
    label: 'Total Interest Paid',
    value: formatCurrency(totalInterest),
    icon: TrendingUp,
    iconColor: 'text-succession-warning',
    helperText: 'Over full loan term',
  },
  {
    label: 'Total Cost of Loan',
    value: formatCurrency(totalCost),
    icon: Calculator,
    iconColor: 'text-succession-secondary',
    helperText: 'Principal + Total Interest',
  },
];

export default function FinancingSummaryCards({
  monthlyPayment,
  interestRate,
  totalInterest,
  totalCost,
  className = '',
}) {
  const summaryCards = cards(monthlyPayment, interestRate, totalInterest, totalCost);

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 ${className}`}>
      {summaryCards.map((card, index) => {
        const Icon = card.icon;
        return (
          <motion.div
            key={card.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.05 }}
            className="bg-succession-bg-primary border border-succession-border rounded-xl p-6 shadow-succession hover:shadow-succession-lg transition-all duration-300"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-2">
                <div className="text-sm font-medium text-succession-text-secondary">{card.label}</div>
                {card.tooltip && (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="w-3.5 h-3.5 text-succession-text-secondary cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent>
                        <p className="text-xs">{card.tooltip}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                )}
              </div>
              <Icon className={`w-5 h-5 ${card.iconColor}`} />
            </div>

            <motion.div
              key={card.value}
              initial={{ scale: 1.05 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.2 }}
              className="text-3xl font-bold text-succession-text-primary mb-1 font-mono"
            >
              {card.value}
            </motion.div>

            {card.helperText && (
              <div className="text-xs text-succession-text-secondary">{card.helperText}</div>
            )}
          </motion.div>
        );
      })}
    </div>
  );
}
